import { useDisclosure } from '@chakra-ui/hooks'
import {
  Tag,
  TagCloseButton,
  TagLabel,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Box,
} from '@chakra-ui/react'
import type { RefObject } from 'react'
import React, { useMemo, useRef, useEffect, useState } from 'react'
import { colors } from '../../../../ui-kit'

import type {
  CustomMetadataResponse,
  FormValidationObject,
  RemoveFilterProps,
  SearchQueryJson,
} from '../../../interfaces'
import FilterText from '../FilterText'
import { getInputForm } from '../InputForms'

interface Props {
  searchQueryJson: SearchQueryJson
  customMetadataFieldsMapping: { [fieldName: string]: CustomMetadataResponse }
  customMetadataField: CustomMetadataResponse
  validationObject: FormValidationObject
  groupId: number
  filterId: number
  maxWidthRef: RefObject<any>
  wrapRef: RefObject<HTMLDivElement>
  setSearchQueryJson(SearchQueryJson: SearchQueryJson): void
  removeFilter(removeFilterProps: RemoveFilterProps): void
  shouldAutoOpen?: boolean
}

const Filter = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    customMetadataFieldsMapping,
    customMetadataField,
    validationObject,
    groupId,
    filterId,
    maxWidthRef,
    wrapRef,
    setSearchQueryJson,
    removeFilter,
    shouldAutoOpen = false,
  } = props

  const tagRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  // Intersection Observer to track visibility
  useEffect(() => {
    if (!tagRef.current || !wrapRef.current) {return}

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsVisible(entry.isIntersecting)
        })
      },
      {
        root: wrapRef.current,
        rootMargin: '0px',
        threshold: 0.1, // Consider visible if at least 10% is shown
      },
    )

    observer.observe(tagRef.current)

    return () => {
      observer.disconnect()
    }
  }, [wrapRef])

  // Only auto-open if shouldAutoOpen is true AND element is visible
  const shouldActuallyOpen = shouldAutoOpen && isVisible

  const {
    isOpen: isOpenFilterForm,
    onClose: onCloseFilterForm,
    onOpen: onOpenFilterForm,
  } = useDisclosure({
    defaultIsOpen: shouldActuallyOpen,
  })

  // Handle opening when visibility changes to true and shouldAutoOpen is true
  useEffect(() => {
    if (shouldAutoOpen && isVisible && !isOpenFilterForm) {
      onOpenFilterForm()
    }
  }, [shouldAutoOpen, isVisible, isOpenFilterForm, onOpenFilterForm])

  // Handle closing when filter becomes not visible
  useEffect(() => {
    if (!isVisible && isOpenFilterForm) {
      onCloseFilterForm()
    }
  }, [isVisible, isOpenFilterForm, onCloseFilterForm])

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId].expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const InputForm = useMemo(
    () =>
      getInputForm({ fieldName: expression.fieldName, customMetadataField }),
    [expression, customMetadataField],
  )

  const maxFilterWidth = useMemo<number | undefined>(
    () => {
      return maxWidthRef?.current?.offsetWidth},
    [maxWidthRef?.current],
  )

  const error: string | null = useMemo(
    () => validationObject.errors[groupId][filterId],
    [validationObject, groupId, filterId],
  )

  const hasValue = useMemo(() => {
    if (Array.isArray(expression.value)) {
      return expression.value.length > 0
    }
    return expression.value !== '' && expression.value !== null
  }, [expression.value])

  const shouldShowActive = hasValue && error === null

  const initialFocusRef = useRef<HTMLElement>(null)

  return (
    <Popover
      placement="bottom-start"
      initialFocusRef={initialFocusRef}
      isOpen={isOpenFilterForm}
      onClose={onCloseFilterForm}
      closeOnBlur={true}
      isLazy={true}
    >
      <PopoverTrigger>
        <Box ref={tagRef} display="inline-block">
          <Tag
            cursor="pointer"
            maxWidth={maxFilterWidth}
            paddingY="6px"
            paddingX="8px"
            h="33px"
            borderRadius="4px"
            borderWidth="1px"
            borderColor={shouldShowActive ? '#cddcf7' : '#e1e4eb'}
            _hover={error === null ? { bgColor: colors.appBlueyGrey } : {}}
            bgColor={shouldShowActive ? '#f3f8ff' : 'unset'}
            onClick={() => {
              onOpenFilterForm()
            }}
          >
            <TagLabel color={colors.appSlate}>
              <FilterText
                {...{
                  searchQueryJson,
                  customMetadataFieldsMapping,
                  validationObject,
                  textColor:
                    error !== null ? '#fd3a57' : colors.imagekitGray[700],
                  groupId,
                  filterId,
                }}
              />
            </TagLabel>
            <TagCloseButton
              onClick={() =>
                void removeFilter({
                  json: searchQueryJson,
                  groupId,
                  filterId,
                })
              }
            />
          </Tag>
        </Box>
      </PopoverTrigger>
      <PopoverContent
        _highlighted={{
          border: 'solid 0.5px #cdd5d',
          borderRadius: '0px',
        }}
        _focus={{ border: 'solid 0.5px #cdd5d', borderRadius: '0px' }}
        _focusVisible={{
          border: 'solid 0.5px #cdd5d',
          borderRadius: '0px',
          outline: 'none',
        }}
        _focusWithin={{
          border: 'solid 0.5px #cdd5d',
          borderRadius: '0px',
        }}
        _active={{ border: 'solid 0.5px #cdd5d', borderRadius: '0px' }}
        borderWidth="0.5px"
        borderColor="#cdd5df"
        borderRadius="0"
        padding="0.75em"
      >
        <InputForm
          searchQueryJson={searchQueryJson}
          validationObject={validationObject}
          customMetadataField={customMetadataField}
          groupId={groupId}
          filterId={filterId}
          initialFocusRef={initialFocusRef}
          setSearchQueryJson={setSearchQueryJson}
          onClose={onCloseFilterForm}
        />
      </PopoverContent>
    </Popover>
  )
}

export default Filter
